import { SelectOptionType } from '@bp/ui-components';
import { distance } from 'fastest-levenshtein';
import type { AscUnresolvedEntity } from '../../../utils/timetable/timetableVersion';

/** Sentinel im Select für „diesen Eintrag bewusst überspringen" (⇒ `targetUuid: null`). */
export const ASC_MAPPING_SKIP = '__skip__';

/**
 * Ab dieser Ähnlichkeit wird ein Treffer vorgeschlagen und das Select vorbefüllt. Bewusst hoch:
 * ein falscher Vorschlag, den niemand bemerkt, importiert Karten auf den falschen Raum — das ist
 * teurer als ein leeres Select, das sofort auffällt.
 */
const MIN_SCORE = 0.82;

/**
 * Mindestabstand zum zweitbesten Treffer. Liegen zwei Kandidaten gleichauf („Raum 1 (EG)" und
 * „Raum 1 (OG)" zu „Raum 1"), ist das genau die Entscheidung, die der Anwender treffen muss.
 */
const AMBIGUITY_MARGIN = 0.05;

/**
 * Basiswert, wenn ein Name vollständig im anderen steckt („Raum 1" in „Raum 1 Altbau"). Der Rest
 * skaliert mit dem Längenverhältnis: je mehr vom längeren Namen der kürzere abdeckt, desto sicherer.
 * Zusammen mit `MIN_SCORE` heißt das: der kürzere Name muss gut 40 % des längeren ausmachen.
 */
const CONTAINED_BASE = 0.7;

/** Vor der Diakritika-Zerlegung, sonst würde aus „ä" nur „a" statt „ae". */
const UMLAUTS: [RegExp, string][] = [
  [/ä/g, 'ae'],
  [/ö/g, 'oe'],
  [/ü/g, 'ue'],
  [/ß/g, 'ss'],
];

/**
 * Der eigentliche Hebel: „Raum 1 (EG)" und „Raum 1 - EG" sind danach identisch. Klammern,
 * Bindestriche und Mehrfach-Leerzeichen sind reine Schreibweise, keine Bedeutung.
 */
function normalize(raw: string): string {
  let value = raw.toLowerCase();
  for (const [pattern, replacement] of UMLAUTS) {
    value = value.replace(pattern, replacement);
  }
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Die Optionslisten sind pro Art identisch und werden von dutzenden Zeilen durchlaufen — ohne
 * Cache würde derselbe Raumname 50-mal normalisiert.
 */
const normalizeCache = new Map<string, string>();

function normalizeCached(raw: string): string {
  const cached = normalizeCache.get(raw);
  if (cached !== undefined) return cached;
  const value = normalize(raw);
  normalizeCache.set(raw, value);
  return value;
}

/** Wortreihenfolge ignorieren: „eg raum 1" trifft „raum 1 eg". */
function tokenSorted(value: string): string {
  return value.split(' ').sort().join(' ');
}

/** Ähnlichkeit in [0, 1]. Gibt 0 zurück, sobald `minScore` nicht mehr erreichbar ist. */
function ratio(a: string, b: string, minScore: number): number {
  const longest = Math.max(a.length, b.length);
  if (longest === 0) return 1;

  // Der Längenunterschied ist eine untere Schranke der Distanz — spart bei hunderten Optionen je
  // Zeile den Großteil der Vergleiche, noch bevor `distance` überhaupt aufgerufen wird.
  const maxDistance = Math.floor(longest * (1 - minScore));
  if (Math.abs(a.length - b.length) > maxDistance) return 0;

  const dist = distance(a, b);
  return dist > maxDistance ? 0 : 1 - dist / longest;
}

function similarity(a: string, b: string): number {
  if (a === b) return 1;

  let best = ratio(a, b, MIN_SCORE);

  const sortedA = tokenSorted(a);
  const sortedB = tokenSorted(b);
  if (sortedA !== a || sortedB !== b) {
    best = Math.max(best, sortedA === sortedB ? 1 : ratio(sortedA, sortedB, MIN_SCORE));
  }

  // Enthaltensein wird an Wortgrenzen geprüft, sonst würde „Raum 1" auch in „Vorraum 1" treffen.
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (shorter.length >= 3 && ` ${longer} `.includes(` ${shorter} `)) {
    best = Math.max(best, CONTAINED_BASE + (1 - CONTAINED_BASE) * (shorter.length / longer.length));
  }

  return best;
}

export type AscSuggestion = {
  /** UUID der vorgeschlagenen Entität. */
  value: string;
  label: string;
  /** Ähnlichkeit in [0, 1]. */
  score: number;
};

/**
 * Bester Treffer für einen nicht auflösbaren Eintrag — oder `null`, wenn keiner sicher genug ist
 * bzw. zwei Kandidaten gleichauf liegen.
 */
export function suggestAscMatch(entry: AscUnresolvedEntity, options: SelectOptionType[]): AscSuggestion | null {
  const source = normalizeCached(entry.name ?? entry.label);
  if (!source) return null;

  let best: AscSuggestion | null = null;
  let runnerUp = 0;

  for (const option of options) {
    // Gesperrte Optionen (von Geschwister-Zeilen belegt) sind keine gültigen Vorschläge.
    if (typeof option.value !== 'string' || option.value === ASC_MAPPING_SKIP || option.disabled) continue;

    const label = String(option.label);
    const score = similarity(source, normalizeCached(label));
    if (score < MIN_SCORE) continue;

    if (!best || score > best.score) {
      if (best) runnerUp = best.score;
      best = { value: option.value, label, score };
    } else if (score > runnerUp) {
      runnerUp = score;
    }
  }

  if (!best || best.score - runnerUp < AMBIGUITY_MARGIN) return null;
  return best;
}
